<script setup>
import { computed, onMounted, ref, watch } from "vue";
import pageTitle, { setPageTitle } from "../../../utils/pageTitle";
import { useToast } from "vue-toastification";
import UpdateLotDialog from "@/components/debug/lot-fixer/UpdateLotDialog.vue";
import ActiveDeleteLotCellRenderer from "@/components/debug/lot-fixer/ActiveDeleteLotCellRenderer.vue";
import BatchUpdateDialog from "@/components/debug/lot-fixer/BatchUpdateDialog.vue";
import useDebug from "@/hooks/useDebug";
import { monthDayYearFormat, yyyyMMddFormat, yearMonthDayFormat } from "@/utils/dateUtils";
import { useUserStore } from "@/stores/user";
// ag-grid
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-material.min.css";
import { AgGridVue } from "ag-grid-vue3";
import { LicenseManager } from "ag-grid-enterprise";
import {
  buildQueryParams,
} from "@/utils/agGridUtils";
import { getDefaultColDefs, getDefaultGridOptions } from "@/utils/aggridCommonConfigs";
import { useThemeStore } from "@/stores/themeStore";
import { useLoadingStore } from "@/stores/loading";
import c3api from "@/c3api";
import useClients from "@/hooks/useClients";
import debounce from "lodash/debounce";
import useWarehouses from "@/hooks/useWarehouses";

const SEARCH_BY_LOT = 'LOT';
const SEARCH_BY_EXPIRY = 'EXPIRY';
const searchByItems = [
  { title: 'Lot', value: SEARCH_BY_LOT },
  { title: 'Expiry', value: SEARCH_BY_EXPIRY },
];

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);
const userStore = useUserStore();
const LD_CLIENT_ID = 10006;
const { clients, fetchClients } = useClients();
const { warehouses, fetchWarehouses  } = useWarehouses();
const { fetchLotBySku, fetchProductBySku, toggleAllDeleted, fetchSkusByWarehouse } = useDebug();
const skus = ref([]);
const toast = useToast();
const themeStore = useThemeStore();
const loadingStore = useLoadingStore();
const loadingLots = ref(false);

const product = ref(null);
const lots = ref([]);
const selectedLpsIds = ref([]);
const selectedCount = ref(0);
const localState = ref({
  client_id: null,
  sku: "",
  lot: "",
  expiry: "",
  showAll: false,
  showAllLots: false,
  searchBy: SEARCH_BY_LOT,
  warehouse_id: 1,
})
const showMainSection = ref(false);

const showBatchDialog = ref(false);
const batchMode = ref(""); 

const canBatchUpdate = computed(() => {
  return !!userStore.user?.permissions?.batch_update_lots;
});

const handleBatchClick = (mode) => {
  if (!canBatchUpdate.value) {
    toast.error("You do not have permission to batch update lots.");
    return;
  }
  batchMode.value = mode;
  showBatchDialog.value = true;
};

const handleBatchUpdated = async () => {
  await refreshTables();
  if (gridApi) {
    gridApi.refreshServerSide({ purge: true });
    gridApi.deselectAll();
  }
};

watch(showBatchDialog, (val) => {
  if (!val) {
    batchMode.value = "";
  }
});

const lotUrl = computed(() => {
  let expiry = localState.value.expiry;
  if(expiry) {
    expiry = yearMonthDayFormat(localState.value.expiry, '-') ?? localState.value.expiry;
  }
  const searchParam = localState.value.searchBy === SEARCH_BY_LOT ? `&lot=${localState.value.lot}` : `&expiry=${expiry}`;
  return `/debug/lps_by_lot?sku=${localState.value.sku}&show_all=${localState.value.showAll}${searchParam}&client_id=${localState.value.client_id}&warehouse_id=${localState.value.warehouse_id}`;
});
const gridLoaded = ref(false);
let gridApi;
let context = ref(null);

const totalPallets = ref(0);
const totalCases = ref(0);
const totalActivePallets = ref(0);
const hasDamagedLps = ref(false);

onMounted( async() => {
  setPageTitle("Lot Reconciliation");
  await fetchClients(clientUrlParams);
  await fetchWarehouses({ filters: [{ column: "active", condition: "eq", value: true }] });

});

const onGridReady = (params) => {
  gridApi = params.api;
  context.value = {
    apiUrl: lotUrl.value,
    product: product.value,
  };
  gridLoaded.value = true;
  gridApi.setGridOption("serverSideDatasource", connectToServerApi());
  gridApi.addEventListener('selectionChanged', updateCounts);
};

const defaultColDef = getDefaultColDefs();
defaultColDef.floatingFilter = false;
defaultColDef.filter = false;

const columnDefs = [
  {
    headerName: "",
    checkboxSelection: true,
    headerCheckboxSelection: true,
    width: 60, 
    minWidth: 60,
    maxWidth: 60,
    cellClass: "ag-cell-center",
    headerClass: "ag-header-center",
    resizable: false,
    suppressHeaderMenuButton: true,
    pinned: "left",
    suppressMenu: true,
    suppressMovable: true
  },
  { field: "warehouse.nickname", headerName: "WH", colId: "warehouse.nickname", cellDataType: "text"},
  { field: "bin.name", headerName: "BIN", colId: "bin.name", cellDataType: "text"},
  { field: "created_at", headerName: "Created At", colId: "created_at", cellDataType: "text", valueFormatter: (params) => yyyyMMddFormat(params.value)},  
  { field: "number", headerName: "LPN", colId: "number", cellDataType: "text",
    cellRenderer: function (params) {
      if (params.data.number) {
        return `<a target="_blank" class="text-decoration-none text-primary" href="${import.meta.env.VITE_CONNECT_URL}/lps/${params.data.id}">${params.data.number}</a>`;
      } else {
        return '';
      }
    },
  },
  {
    field: "lot",
    headerName: "Lot",
    colId: "lot",
    cellRenderer: UpdateLotDialog,
    cellRendererParams: {
      onFieldUpdated: () => {
        refreshTables();
      }
    }
  },
  {
    field: "expiry",
    headerName: "Expiry",
    colId: "expiry",
    cellRenderer: UpdateLotDialog,
    cellRendererParams: {
      onFieldUpdated: () => {
        refreshTables();
      }
    }
  },
  {
    headerName: "Current Qty",
    colId: "pallet_qty",
    cellDataType: "text",
    valueGetter: (params) => getLpQuantity(params.data),
  },
  {
    cellDataType: "text",
    minWidth: 200,      
    cellRenderer: ActiveDeleteLotCellRenderer,
    cellRendererParams: {
      onLpToggled: (params) => {
        refreshTables();
      }
    }
  },
];

const gridOptions = getDefaultGridOptions(columnDefs);

gridOptions.autoSizeStrategy = {
  type: "fitCellContents",
};
gridOptions.sideBar = {};
gridOptions.rowSelection = "multiple";
gridOptions.rowMultiSelectWithClick = true;

const onSearch = async () => {
  const fieldsToCheck = [
    { value: localState.value.client_id, message: "Please select a client." },
    { value: localState.value.sku, message: "Please enter a SKU." },
    { value: localState.value.warehouse_id, message: "Please select a warehouse." },
  ];

  for (const field of fieldsToCheck) {
    if (!field.value) {
      toast.error(field.message);
      showMainSection.value = false;
      return;
    }
  }

  loadingStore.state(true);
  try {
    product.value = await fetchProductBySku({sku: localState.value.sku, client_id: localState.value.client_id, warehouse_id: localState.value.warehouse_id});
    
    showMainSection.value = !!product.value;
    
    if (showMainSection.value) {
      await fetchLots();
      if (gridApi) {
        context.value.apiUrl = lotUrl.value;
        context.value.product = product.value;
        gridApi.refreshServerSide({ purge: true });
      }
    }
  } finally {
    loadingStore.state(false);
  }
}

const selectValue = (value) => {
  window.scrollTo(0, 0);
  if (localState.value.searchBy === SEARCH_BY_LOT) {
    localState.value.lot = value;
  } else {
    localState.value.expiry = value;
  }
  if (gridApi) {
    context.value.apiUrl = lotUrl.value;
    context.value.product = product.value;
    gridApi.refreshServerSide({ purge: true });
  }
}

const fetchLots = async () => {
  lots.value = await fetchLotBySku({sku: localState.value.sku, show_all: localState.value.showAllLots, client_id: localState.value.client_id, warehouse_id: localState.value.warehouse_id, group_by: localState.value.searchBy === SEARCH_BY_LOT ? 'lot' : 'expiry'});
}

const expectedCs = computed(() => product.value?.meta?.totals ? product.value.meta.totals.on_hand - product.value.meta.totals.allocated : 0);
const changeNeededCs = computed(() => product.value?.meta ? (product.value.meta.verified_cs - expectedCs.value).toFixed(3) : 0.000);

const updateCounts = (params) => {
  const api = (params && params.api) || gridApi;
  
  const allRows = [];
  if (api && api.forEachNode) {
    api.forEachNode(node => {
      if (node.data) {
        allRows.push(node.data);
      }
    });
  }

  totalPallets.value = allRows.length;
  totalCases.value = allRows.reduce((acc, curr) => {
    let qty = 0;
    qty = getLpQuantity(curr);
    return acc + qty;
  }, 0);
  totalActivePallets.value = allRows.filter(row => !row.deleted).length;
  let selected = [];
  if (api && api.getSelectedRows) {
    selected = api.getSelectedRows();
  }

  if ((!selected || selected.length === 0) && api && api.forEachNode) {
    const tmp = [];
    api.forEachNode((node) => {
      if (node && node.isSelected && node.isSelected()) {
        if (node.data) tmp.push(node.data);
      }
    });
    selected = tmp;
  }

  selectedLpsIds.value = (selected || []).map(r => r.id);
  selectedCount.value = (selected || []).length;

  hasDamagedLps.value = (selected || []).some(row => row.bin?.holds_destructable_damage || row.bin?.holds_processable_damage);
}

const toggleAll = async (new_value) => {
  if (hasDamagedLps.value) {
    toast.error("This action cannot be performed as some LPs have damaged LPNs.");
    return;
  }
  loadingStore.state(true);
  try {
    const response = await toggleAllDeleted(selectedLpsIds.value, new_value);
    if (gridApi && response) {
      await refreshTables();
      gridApi.refreshServerSide({ purge: true });
      gridApi.deselectAll();
    }
  } finally {
    loadingStore.state(false);
  }
}

const refreshTables = async () => {
  product.value = await fetchProductBySku({sku: localState.value.sku, client_id: localState.value.client_id, warehouse_id: localState.value.warehouse_id});
  await fetchLots();
}

watch(
  () => localState.value.showAllLots,
  async (newVal) => {
    loadingLots.value = true;
    try {
      await fetchLots();
    } finally {
      loadingLots.value = false;
    }    
  }
);

const updatedLots = computed(() => {
  if (!lots.value) return [];
  return lots.value.map(lot => {
    const lotIsInvalid = !lot.lot || lot.lot === 'null' || lot.lot === 'nil';
    const expiryIsInvalid = !lot.expiry || lot.expiry === 'null' || lot.expiry === 'nil';

    if (lotIsInvalid || expiryIsInvalid) {
      return {
        ...lot,
        lot: lotIsInvalid ? 'NA' : lot.lot,
        expiry: expiryIsInvalid ? 'NA' : lot.expiry,
      };
    }

    return {
      ...lot,
      expiry: monthDayYearFormat(lot.expiry) ?? lot.expiry,
    };
  });
});

const getLpQuantity = (lpData) => {
  // if Lp has multiple items then we match the product and sum the items.qty
  if (lpData.items?.length > 1) {
    if (product.value?.data?.id) {
      return lpData.items
        .filter(item => item.product_id === product.value.data.id)
        .reduce((sum, item) => sum + item.qty, 0);
    }
    return 0;
  }

  if (localState.value.client_id === LD_CLIENT_ID) {
    //LD clients use pallet_qty instead of item_qty because some old LPs do not have item
    return lpData.pallet_qty ?? lpData.items?.[0]?.qty ?? 0;
  }

  // For other clients, prefer item qty but fallback to pallet_qty.
  return lpData.items?.[0]?.qty ?? lpData.pallet_qty ?? 0;
};

const connectToServerApi = function () {
  return {
    getRows: async (params) => {
      const gridApi = params.api;
      try {
        const context = params.context;
        const urlParams = buildQueryParams(
          params,
          context.filterKey,
          context.advancedFilterKeys,
        );

        const api = c3api;
        const response = await api.get(context.apiUrl, { params: urlParams });
        const data = response.data;

        params.success({
          rowData: data.data,
          rowCount: data.total_count,
        });

        if (data.total_count === 0) {
          gridApi.showNoRowsOverlay();
        } else {
          gridApi.hideOverlay();
        }
      } catch (error) {
        toast.error(error.response.data?.data?.join(". ") ?? error.response?.statusText);
        params.fail();
      }
    },
    maxConcurrentDatasourceRequests: 1,
  };
};

const clientUrlParams = {
  filter: {
    name: {
      type: "contains",
      filter: "",
    },
  },
  sort: [
    {
      sort: "asc",
      colId: "name",
    },
  ],
  limit: 500,
  lot_fixer: true,
};

const debouncedFetchClients = debounce(async (query) => {
  clientUrlParams.filter.name.filter = query;
  await fetchClients(clientUrlParams);
}, 300);

const productSkus = computed(() =>
  (skus.value ?? [])
    .map((s) => s.sku)
    .filter((sku) => !!sku)
);

const fetchClientSkus = async () => {
  if (!localState.value.client_id || !localState.value.warehouse_id) {
    skus.value = [];
    return;
  }
  skus.value = await fetchSkusByWarehouse({
    client_id: localState.value.client_id,
    warehouse_id: localState.value.warehouse_id,
  }) ?? [];
};

const onClientSelected = () => {
  resetLocalState();
  fetchClientSkus();
};

watch(
  () => localState.value.warehouse_id,
  () => {
    if (!localState.value.client_id) return;
    localState.value.sku = "";
    fetchClientSkus();
  }
);

const resetLocalState = () => {
  localState.value.sku = "";
  localState.value.lot = "";
  localState.value.expiry = "";
  localState.value.showAll = false;
  localState.value.showAllLots = false;
  showMainSection.value = false;
}
</script>

<template>
  <div class="py-4">
    <v-card-title class="d-flex justify-space-between"
      ><span>{{ pageTitle }}</span
      >      
      <v-select
        label="Search lps by lot or expiry date:"
        v-model="localState.searchBy"
        :items="searchByItems"
        @update:modelValue="resetLocalState"
        item-title="title"
        item-value="value"
        variant="outlined"
        density="compact"
        max-width="250px"
        color="indigo-lighten-2"
        hide-details="auto"
      />
    </v-card-title>
    <v-card flat class="mx-4 px-4 mt-4">
      <!-- SEARCH SECTION -->
      <v-row class="py-4">
        <v-col cols="12" sm="9">
          <div class="d-flex ga-6 w-100 align-center">
            <v-autocomplete 
              v-model="localState.client_id"
              color="indigo-lighten-2"
              variant="outlined"
              density="compact" 
              :items="clients"
              @update:search="(query) => debouncedFetchClients(query)"
              @update:modelValue="onClientSelected"
              item-title="name"
              item-value="id"
              id="client"
              max-width="400"
              autocomplete="off"
              hide-details="auto" 
            >
              <template v-slot:label>
                Select a Client <span class="text-red">*</span>
              </template>
            </v-autocomplete>

            <template v-if="localState.client_id">

              <v-select 
                v-model="localState.warehouse_id"
                color="indigo-lighten-2"
                variant="outlined"
                density="compact" 
                :items="warehouses.data"
                item-title="nickname" 
                item-value="id"
                id="client"
                autocomplete="off"
                hide-details="auto" 
              >
                <template v-slot:label>
                  Warehouse <span class="text-red">*</span>
                </template>
              </v-select>

              <v-autocomplete
                v-model="localState.sku"
                :items="productSkus"
                density="compact"
                variant="outlined"
                color="indigo-lighten-2"
                hide-details="auto"
                auto-select-first
                autocomplete="off"
                flat
                @keydown.enter="onSearch"
              >
                <template v-slot:label>
                  SKU <span class="text-red">*</span>
                </template>
              </v-autocomplete>

              <v-text-field
                v-if="localState.searchBy === SEARCH_BY_LOT"
                v-model="localState.lot"
                density="compact"
                variant="outlined"
                color="indigo-lighten-2"
                label="LOT"
                hide-details="auto"
                flat
                @keydown.enter="onSearch"
              ></v-text-field>

              <v-text-field
                v-else
                v-model="localState.expiry"
                density="compact"
                variant="outlined"
                color="indigo-lighten-2"
                label="EXPIRY DATE"
                placeholder="MM-DD-YYYY"
                hide-details="auto"
                flat
                @keydown.enter="onSearch"
              ></v-text-field>
              <v-checkbox v-model="localState.showAll" label="Show All LPNs" hide-details="auto" color="indigo-lighten-2" flat></v-checkbox>
            </template>
          </div>
        </v-col>
        <v-col cols="12" sm="3">
          <div v-if="localState.client_id" class="d-flex align-center ga-6 w-100 h-100 justify-center justify-sm-end">
            <v-btn width="120" color="blue" @click="onSearch">GO</v-btn>
          </div>
        </v-col>
      </v-row>

      <v-divider class="mb-4" v-if="showMainSection"></v-divider>
      <section v-if="showMainSection">
        <h1>{{product.data.name}} / {{product.data.sku}} - {{ product.meta.lps }} Plts -- {{product.data.units_per_units1}} - {{product.data.units1_per_units2}}</h1>
        <v-row  class="my-4">

          <v-col cols="12" lg="9">
              <v-sheet border rounded>              
                <v-card-title class="bg-surface-light">
                  <div class="mb-0 d-flex justify-space-between">
                    <strong>Lot "{{localState.searchBy === SEARCH_BY_LOT ? localState.lot : localState.expiry}}" LPs in ALL BINS</strong>
                    <span>ACTIVE PALLETS: {{totalActivePallets}}</span>
                    <small>Cases: {{totalCases.toFixed(3)}} / Pallets: {{totalPallets}}</small>
                  </div>
                </v-card-title>
                <AgGridVue style="width: 100%; height: 450px;" :class="themeStore.agTheme" :columnDefs="columnDefs" :defaultColDef="defaultColDef" @model-updated="updateCounts"
                  :gridOptions="gridOptions" @grid-ready="onGridReady" :context="context" overlayNoRowsTemplate="<span class='text-gray-500'>No data available.</span>"
                  >
                </AgGridVue>
                <div class="d-flex ga-4 pa-4 flex-column flex-md-row">
                  <v-btn :disabled="selectedCount === 0" color="success" @click="toggleAll(false)">SET ALL ACTIVE</v-btn>
                  <v-btn :disabled="selectedCount === 0" color="error" @click="toggleAll(true)">SET ALL DELETED</v-btn>
                  <v-btn class="mt-2 mt-md-0" :disabled="selectedCount === 0" color="primary" @click="handleBatchClick('lot')">BATCH UPDATE LOT</v-btn>
                  <v-btn class="mt-2 mt-md-0" :disabled="selectedCount === 0" color="secondary" @click="handleBatchClick('expiry')">BATCH UPDATE EXPIRY</v-btn>
                </div>
                <BatchUpdateDialog
                  v-model="showBatchDialog"
                  :selectedIds="selectedLpsIds"
                  :mode="batchMode"
                  @updated="handleBatchUpdated"
                />
              </v-sheet>
          </v-col>

          <v-col cols="12" lg="3">
            <v-sheet border rounded>
              <v-card-title class="bg-surface-light">{{product.data.customer_sku}} Totals</v-card-title>
              <v-table density="compact">
                <tbody> 
                  <tr align="right">
                    <th class="font-weight-bold border-b">ON HAND</th>
                    <td class="border-b">{{product?.meta?.totals?.on_hand}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold border-b">AVAILABLE</th>
                    <td class="border-b">{{product?.meta?.totals?.available}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold border-b">RESERVED <br> <small>Allocated but not picked</small></th>
                    <td class="border-b">{{product?.meta?.totals?.reserved}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold border-b">PICKED <br><small>Allocated and picked</small></th>
                    <td class="border-b">{{product?.meta?.totals?.allocated}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold border-b">EXPECTED CS ON LPS</th>
                    <td class="border-b">{{expectedCs}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold border-b">VERIFIED</th>
                    <td class="border-b">{{product.meta.verified_cs}}</td>
                  </tr>
                  <tr align="right">
                    <th class="font-weight-bold">CHANGE NEEDED <br><small>Extra LP Cases</small></th>
                    <td>{{changeNeededCs}}</td>
                  </tr>
                </tbody>
              </v-table>
            </v-sheet>

            <v-sheet border rounded  class="mt-6">

              <div class="d-flex flex-column justify-center align-center my-10" v-if="loadingLots">
                <v-progress-circular
                  :width="10"
                  :size="100"
                  color="teal"
                  indeterminate
                ></v-progress-circular>
              </div>
              <template v-else>
                <v-card-title class="bg-surface-light d-flex justify-space-between align-center">
                  <div>{{product.data.customer_sku}} LOTS</div>
                  <v-checkbox v-model="localState.showAllLots" label="Show All Lots" hide-details="auto" density="compact" color="indigo-lighten-2" class="font-weight-bold" flat></v-checkbox>
                </v-card-title>
                <v-table density="compact">
                  <thead>
                    <tr>
                      <th class="font-weight-bold border-b">LOT/EXP DATE</th>
                      <th class="font-weight-bold border-b">CS</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(lot, index) in updatedLots" :key="`${lot.lot}-${lot.expiry}-${lot.case_qty}`">
                      <th v-if="localState.searchBy === SEARCH_BY_LOT" @click="selectValue(lot.lot)" class="text-blue cursor-pointer" :class="{'border-b': index !== updatedLots.length - 1}">{{lot.lot}}</th>
                      <th v-else @click="selectValue(lot.expiry)" class="text-blue cursor-pointer" :class="{'border-b': index !== updatedLots.length - 1}">{{lot.expiry}}</th>
                      <td :class="{'border-b': index !== updatedLots.length - 1}">{{lot.case_qty}}</td>
                    </tr>
                  </tbody>
                </v-table>
              </template>
            </v-sheet>
          </v-col>
        </v-row>
      </section>
    </v-card>
  </div>
</template>